/**
 * MifeLife v2 — Mentor marketplace.
 *
 * Lists all 10 founder cohort mentors with filters (specialty, language, max rate).
 * If user has a recent analysis (via ?focus param), AI-match boosts mentors aligned
 * with their weakest dimension.
 *
 * Booking modal: select day/time slot → "Ödemeye Geç" → POST /v2/sessions/book.
 * Auth required for booking.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Star, Filter, X, Calendar, Loader2, Lock, CheckCircle2, ShieldCheck, Sparkles, Globe } from 'lucide-react';

const LANG_LABEL = { tr: 'Türkçe', en: 'İngilizce', de: 'Almanca', fr: 'Fransızca', es: 'İspanyolca' };
const langText = (codes) => (codes || []).map((c) => LANG_LABEL[c] || (c || '').toUpperCase()).join(', ');
import V2Nav from '../../components/v2/V2Nav';
import V2Footer from '../../components/v2/V2Footer';
import { apiRequest, getToken } from '../../lib/utils';

const T = {
  eyebrow: 'UZMAN MENTOR PAZARYERİ',
  heading: 'Sana uygun mentorlarla tanış',
  sub: "Türkiye ve Avrupa'dan doğrulanmış uzman mentorlar. AI eşleştirme skoruyla sıralanır.",
  filters: 'Filtreler', clearFilters: 'Temizle',
  specialty: 'Uzmanlık', language: 'Dil', maxRate: 'Maks. saatlik (₺)',
  all: 'Tümü',
  bookCta: 'Rezervasyon yap', perHour: '/ saat',
  modalTitle: 'Mentör seansı',
  modalDate: 'Tarih seç', modalTime: 'Saat seç', modalDuration: 'Süre',
  needAuth: 'Rezervasyon için giriş yapmalısın.',
  needAuthCta: 'Hızlı kayıt ol',
  confirm: 'Ödemeye geç →',
  successTitle: 'Rezervasyonun oluşturuldu',
  successSub: 'Mentörle görüşme linkin aşağıda.',
  goJitsi: 'Jitsi seansa katıl',
  goDashboard: 'Panele git',
  empty: 'Bu filtrelere uygun mentör bulunamadı.',
};

const DAY_NAMES = ['Pzr', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];

export default function V2MentorsPage() {
  const [params] = useSearchParams();
  const focusFromAnalysis = params.get('focus');
  const [mentors, setMentors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ specialty: params.get('specialty') || '', language: params.get('language') || '', max_rate: '' });
  const [specialtyOpts, setSpecialtyOpts] = useState([]);
  const [bookFor, setBookFor] = useState(null);

  const refresh = () => {
    setLoading(true);
    const qp = new URLSearchParams();
    if (filters.specialty) qp.set('specialty', filters.specialty);
    if (filters.language)  qp.set('language', filters.language);
    if (filters.max_rate)  qp.set('max_rate', filters.max_rate);
    if (focusFromAnalysis) qp.set('analysis_focus', focusFromAnalysis);
    apiRequest(`/v2/mentors/?${qp.toString()}`)
      .then((r) => setMentors(r?.items || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filters, focusFromAnalysis]);
  useEffect(() => {
    apiRequest('/v2/mentors/specialties').then((r) => setSpecialtyOpts(r?.items || []));
    document.title = 'Mentörler · MifeLife';
  }, []);

  const clearFilters = () => setFilters({ specialty: '', language: '', max_rate: '' });

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0F0F1A] via-[#11111E] to-[#1A1A2E] text-white antialiased" data-testid="v2-mentors-page">
      <V2Nav lang="tr" />
      <section className="pt-28 sm:pt-32 pb-12 border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-500/10 border border-violet-400/30 text-[11px] uppercase tracking-[0.18em] text-violet-200 mb-5">
            {T.eyebrow}
          </div>
          <h1 className="text-3xl sm:text-5xl font-semibold tracking-tight leading-tight">{T.heading}</h1>
          <p className="mt-4 text-base text-slate-400 max-w-2xl">{T.sub}</p>
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-6">
          {/* Filters */}
          <aside className="lg:col-span-3" data-testid="v2-mentors-filters">
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 sticky top-24">
              <div className="flex items-center justify-between mb-5">
                <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 flex items-center gap-2"><Filter className="w-3 h-3" /> {T.filters}</div>
                <button onClick={clearFilters} className="text-[11px] text-slate-400 hover:text-white transition" data-testid="v2-filters-clear">{T.clearFilters}</button>
              </div>
              <FilterField label={T.specialty}>
                <select value={filters.specialty} onChange={(e) => setFilters((f) => ({ ...f, specialty: e.target.value }))} className="w-full h-10 px-3 rounded-lg bg-[#0a0a14] border border-white/10 text-sm text-white" data-testid="v2-filter-specialty">
                  <option value="">{T.all}</option>
                  {specialtyOpts.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </FilterField>
              <FilterField label={T.language}>
                <select value={filters.language} onChange={(e) => setFilters((f) => ({ ...f, language: e.target.value }))} className="w-full h-10 px-3 rounded-lg bg-[#0a0a14] border border-white/10 text-sm text-white" data-testid="v2-filter-lang">
                  <option value="">{T.all}</option>
                  <option value="tr">Türkçe</option>
                  <option value="en">English</option>
                  <option value="de">Deutsch</option>
                </select>
              </FilterField>
              <FilterField label={T.maxRate}>
                <input type="number" value={filters.max_rate} onChange={(e) => setFilters((f) => ({ ...f, max_rate: e.target.value }))} placeholder="örn. 500" className="w-full h-10 px-3 rounded-lg bg-[#0a0a14] border border-white/10 text-sm text-white placeholder:text-slate-600" data-testid="v2-filter-rate" />
              </FilterField>
            </div>
          </aside>

          {/* List */}
          <div className="lg:col-span-9">
            {loading ? (
              <div className="py-20 text-center"><Loader2 className="w-7 h-7 text-violet-300 animate-spin mx-auto" /></div>
            ) : mentors.length === 0 ? (
              <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center text-slate-400">{T.empty}</div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4" data-testid="v2-mentors-grid">
                {mentors.map((m) => <MentorCard key={m.id} m={m} onBook={() => setBookFor(m)} />)}
              </div>
            )}
          </div>
        </div>
      </section>

      {bookFor && <BookModal mentor={bookFor} onClose={() => setBookFor(null)} />}

      <V2Footer lang="tr" />
    </div>
  );
}

const FilterField = ({ label, children }) => (
  <div className="mb-4">
    <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-2">{label}</div>
    {children}
  </div>
);

const MentorCard = ({ m, onBook }) => {
  const disc = m.first_session_discount;
  const hasDisc = disc?.enabled && Number(disc.pct) >= 5;
  return (
  <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 hover:border-white/20 hover:bg-white/[0.04] transition" data-testid={`v2-mentor-card-${m.id}`}>
    <div className="flex items-start gap-4">
      <Link to={`/m/${m.slug || m.id}`} className="flex-shrink-0 block">
        <img src={m.photo_url} alt={m.name} className="w-16 h-16 rounded-2xl object-cover hover:opacity-90 transition" />
      </Link>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <Link to={`/m/${m.slug || m.id}`} className="text-base font-semibold tracking-tight hover:text-violet-300 transition" data-testid={`v2-mentor-name-link-${m.id}`}>{m.name}</Link>
          {m.verified && <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-violet-500/15 border border-violet-400/40 text-[9px] font-semibold text-violet-200" title="Doğrulanmış mentör" data-testid={`v2-mentor-verified-${m.id}`}><ShieldCheck className="w-2.5 h-2.5" strokeWidth={2.4} />Doğrulanmış</span>}
          {hasDisc && <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-[9px] font-semibold text-emerald-200" title="İlk seans indirimi" data-testid={`v2-mentor-discount-${m.id}`}><Sparkles className="w-2.5 h-2.5" strokeWidth={2.4} />İlk seans %{disc.pct}</span>}
        </div>
        <div className="text-sm text-slate-400 mb-2">{m.title} · {m.city}</div>
        <div className="flex items-center gap-2 text-xs text-slate-400 mb-3 flex-wrap" data-testid={`v2-mentor-stats-${m.id}`}>
          {m.review_count > 0 ? (
            <>
              <span className="inline-flex items-center gap-1 text-amber-200"><Star className="w-3 h-3 fill-amber-400 text-amber-400" />{m.rating}</span>
              <span className="text-slate-600">·</span>
              <span>{m.review_count} değerlendirme</span>
            </>
          ) : (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-200 text-[10px] font-semibold" data-testid={`v2-mentor-new-${m.id}`}><Sparkles className="w-2.5 h-2.5" />Yeni Mentör</span>
          )}
          {m.completed_sessions > 0 && (
            <>
              <span className="text-slate-600">·</span>
              <span data-testid={`v2-mentor-sessions-${m.id}`}>{m.completed_sessions} görüşme</span>
            </>
          )}
          <span className="text-slate-600">·</span>
          <span className="text-violet-300 font-medium">AI Uyum: %{m.ai_match_pct}</span>
        </div>
        <p className="text-[13px] text-slate-300 leading-relaxed mb-3 line-clamp-2">{m.bio_short}</p>
        {(m.languages?.length > 0) && (
          <div className="flex items-center gap-1.5 text-[11px] text-slate-400 mb-2" data-testid={`v2-mentor-langs-${m.id}`}>
            <Globe className="w-3 h-3 text-fuchsia-300" />
            <span>{langText(m.languages)}</span>
            <span className="ml-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-fuchsia-500/12 border border-fuchsia-400/25 text-fuchsia-200 text-[9px] font-semibold uppercase tracking-wide">Global · Beta</span>
          </div>
        )}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {m.specialties.map((s) => (
            <span key={s} className="text-[10px] px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/10 text-slate-300">{s}</span>
          ))}
        </div>
      </div>
    </div>
    <div className="flex items-center justify-between pt-4 border-t border-white/[0.06]">
      <div>
        <span className="text-lg font-semibold tabular-nums">₺{m.hourly_rate_try}</span>
        <span className="text-xs text-slate-500 ml-1">{T.perHour}</span>
      </div>
      <div className="flex items-center gap-2">
        <Link to={`/m/${m.slug || m.id}`} className="text-xs text-slate-300 hover:text-violet-300 transition px-2" data-testid={`v2-mentor-profile-${m.id}`}>Profili gör →</Link>
        <button onClick={onBook} className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-gradient-to-r from-violet-500 to-violet-600 text-white text-sm font-medium hover:from-violet-400 hover:to-violet-500 transition" data-testid={`v2-mentor-book-${m.id}`}>
          {T.bookCta}
        </button>
      </div>
    </div>
  </div>
  );
};

// ---------- Booking modal ----------
const BookModal = ({ mentor, onClose }) => {
  const nav = useNavigate();
  const authed = !!getToken();
  const [date, setDate] = useState(null);   // 'YYYY-MM-DD'
  const [time, setTime] = useState(null);    // 'HH:MM'
  const [slots, setSlots] = useState([]);
  const [vacation, setVacation] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null); // session record
  const [err, setErr] = useState(null);
  const [quote, setQuote] = useState(null);

  useEffect(() => {
    if (!authed) return;
    apiRequest(`/v2/sessions/quote/${mentor.id}`).then(setQuote).catch(() => {});
  }, [mentor.id, authed]);

  useEffect(() => {
    setSlotsLoading(true);
    apiRequest(`/v2/sessions/slots/${mentor.id}?days=21`)
      .then((r) => { setSlots(r?.slots || []); setVacation(!!r?.vacation_mode); })
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [mentor.id]);

  const days = React.useMemo(() => {
    const acc = {};
    slots.forEach((s) => {
      acc[s.date] = acc[s.date] || { date: s.date, times: [] };
      acc[s.date].times.push(s.time);
    });
    return Object.values(acc).map((d) => ({ ...d, times: d.times.sort() }));
  }, [slots]);

  const submit = async () => {
    if (!date || !time) return;
    if (!authed) {
      try { sessionStorage.setItem('v2_book_intent', JSON.stringify({ mentor_id: mentor.id, slot: { date, time } })); } catch {}
      nav('/kayit?after=mentor');
      return;
    }
    setSubmitting(true); setErr(null);
    try {
      const res = await apiRequest('/v2/sessions/checkout', {
        method: 'POST',
        body: JSON.stringify({ mentor_id: mentor.id, date, time, duration_min: 60 }),
      });
      if (res?.paymentPageUrl) {
        window.location.assign(res.paymentPageUrl);
        return;
      }
      setErr(res?.message || 'Ödeme başlatılamadı.');
    } catch (e) {
      setErr(e?.message || 'Rezervasyon başarısız.');
      if (e?.status === 409) {
        apiRequest(`/v2/sessions/slots/${mentor.id}?days=21`).then((r) => { setSlots(r?.slots || []); setDate(null); setTime(null); }).catch(() => {});
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-5 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="v2-book-modal">
      <div className="w-full max-w-xl rounded-2xl border border-white/10 bg-[#0F0F1A] p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-violet-300 mb-1">{T.modalTitle}</div>
            <div className="flex items-center gap-3">
              <img src={mentor.photo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
              <div>
                <div className="text-base font-semibold">{mentor.name}</div>
                <div className="text-xs text-slate-400">{mentor.title}</div>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-white" data-testid="v2-book-close"><X className="w-5 h-5" /></button>
        </div>

        {confirmed ? (
          <div data-testid="v2-book-success" className="py-4">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              <div className="text-base font-semibold">{T.successTitle}</div>
            </div>
            <p className="text-sm text-slate-400 mb-5">{T.successSub}</p>
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 mb-5">
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-1">Görüşme linki</div>
              <a href={confirmed.jitsi_link} target="_blank" rel="noopener noreferrer" className="text-sm text-violet-300 underline underline-offset-2 break-all" data-testid="v2-book-jitsi">{confirmed.jitsi_link}</a>
            </div>
            <div className="flex flex-wrap gap-3">
              <a href={confirmed.jitsi_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-violet-500 text-white text-sm font-medium hover:bg-violet-400 transition">{T.goJitsi}</a>
              <button onClick={() => nav('/app')} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg border border-white/15 bg-white/[0.04] text-sm text-slate-200 hover:bg-white/[0.08] transition">{T.goDashboard}</button>
            </div>
          </div>
        ) : !authed ? (
          <div className="py-6 text-center">
            <Lock className="w-7 h-7 text-violet-300 mx-auto mb-3" />
            <div className="text-base mb-2">{T.needAuth}</div>
            <Link to="/kayit?after=mentor" className="inline-flex items-center h-10 px-4 rounded-lg bg-violet-500 text-white text-sm font-medium mt-3" data-testid="v2-book-auth-cta">{T.needAuthCta}</Link>
          </div>
        ) : (
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-3">{T.modalDate} + {T.modalTime}</div>
            {slotsLoading ? (
              <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 text-violet-300 animate-spin" /></div>
            ) : vacation ? (
              <div className="rounded-lg border border-amber-400/30 bg-amber-500/[0.06] p-4 text-sm text-amber-200 mb-5" data-testid="v2-modal-vacation">Bu mentör şu anda izinde. Yakında tekrar müsait olacak.</div>
            ) : days.length === 0 ? (
              <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4 text-sm text-slate-400 mb-5" data-testid="v2-modal-no-slots">Şu an uygun saat yok. Lütfen daha sonra tekrar dene.</div>
            ) : (
              <>
                <div className="grid grid-cols-7 gap-1.5 mb-4">
                  {days.slice(0, 14).map((d) => {
                    const dt = new Date(d.date);
                    const sel = date === d.date;
                    return (
                      <button
                        key={d.date}
                        onClick={() => { setDate(d.date); setTime(null); }}
                        className={`flex flex-col items-center py-1.5 rounded-md border transition ${sel ? 'border-violet-400 bg-violet-500/15 text-white' : 'border-white/[0.06] bg-white/[0.02] text-slate-400 hover:text-white'}`}
                        data-testid={`v2-modal-day-${d.date}`}
                      >
                        <span className="text-[10px] text-slate-500">{DAY_NAMES[dt.getDay()]}</span>
                        <span className="text-sm font-semibold">{dt.getDate()}</span>
                      </button>
                    );
                  })}
                </div>
                {date && (
                  <div className="grid grid-cols-4 gap-1.5 mb-5">
                    {(days.find((dd) => dd.date === date)?.times || []).map((t) => (
                      <button
                        key={t}
                        onClick={() => setTime(t)}
                        className={`py-2 rounded-md text-[12px] tabular-nums border transition ${time === t ? 'border-violet-400 bg-violet-500/15 text-white' : 'border-white/[0.06] bg-white/[0.02] text-slate-300 hover:bg-white/[0.05]'}`}
                        data-testid={`v2-modal-time-${t.replace(':', '')}`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}

            {err && <div className="text-sm text-rose-300 mb-3">{err}</div>}

            <div className="flex items-center justify-between pt-4 border-t border-white/[0.06]">
              {(() => {
                const q60 = quote?.quotes?.find((x) => x.duration_min === 60);
                const discounted = quote?.eligible && q60 && q60.final_price < q60.base_price;
                return (
                  <div className="text-xs text-slate-400">
                    {discounted ? (
                      <div className="flex items-center gap-2 flex-wrap" data-testid="v2-book-discount">
                        <span className="text-sm text-slate-500 line-through tabular-nums">₺{q60.base_price}</span>
                        <span className="text-lg font-semibold text-emerald-300 tabular-nums">₺{q60.final_price}</span>
                        <span className="ml-0.5">/ 60dk</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-200 font-semibold">İlk seans %{quote.discount_pct}</span>
                      </div>
                    ) : (
                      <>
                        <span className="text-lg font-semibold text-white tabular-nums">₺{q60 ? q60.base_price : mentor.hourly_rate_try}</span>
                        <span className="ml-1">/ 60dk</span>
                      </>
                    )}
                  </div>
                );
              })()}
              <button onClick={submit} disabled={!date || !time || submitting} className="inline-flex items-center gap-1.5 h-11 px-5 rounded-lg bg-gradient-to-r from-violet-500 to-violet-600 text-white text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:from-violet-400 hover:to-violet-500 transition" data-testid="v2-book-confirm">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calendar className="w-4 h-4" />}
                {T.confirm}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
